import React, { useMemo, useState } from "react";
import { addWishlistItem, deleteWishlistItem } from "../lib/wishlist";
import { T } from "../theme";
import { Btn, Card, Input, SectionTitle, Select } from "../ui";

const PRIORITIES = ["low", "medium", "high"];
const PRIORITY_COLOR = { low: T.muted, medium: T.accentAlt, high: T.accent };

const emptyForm = { artist: "", albumTitle: "", preferredYear: "", variation: "", targetPrice: "", priority: "medium", notes: "" };

function normalize(s) {
  return (s || "").toLowerCase().trim();
}

export default function WishlistScreen({ ownerUserId, wishlist, activeListings, tier, onViewListing }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [removingId, setRemovingId] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const matchesByWishlistId = useMemo(() => {
    if (!tier.wishlistAlerts) return {};
    const map = {};
    for (const item of wishlist) {
      const match = activeListings.find(
        (l) => l.sellerUserId !== ownerUserId && normalize(l.artist) === normalize(item.artist) && normalize(l.albumTitle) === normalize(item.albumTitle)
      );
      if (match) map[item.id] = match;
    }
    return map;
  }, [wishlist, activeListings, tier.wishlistAlerts, ownerUserId]);

  const add = async () => {
    if (!form.artist || !form.albumTitle) return;
    await addWishlistItem(ownerUserId, {
      artist: form.artist,
      albumTitle: form.albumTitle,
      preferredYear: form.preferredYear ? Number(form.preferredYear) : null,
      variation: form.variation || null,
      targetPrice: form.targetPrice !== "" ? Number(form.targetPrice) : null,
      currencyCode: "USD",
      priority: form.priority,
      notes: form.notes || null,
      notificationsEnabled: false,
    });
    setForm(emptyForm);
    setAdding(false);
  };

  const remove = async (id) => {
    await deleteWishlistItem(id);
    setRemovingId(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: T.muted, letterSpacing: "0.2em", textTransform: "uppercase" }}>{wishlist.length} tapes wanted</div>
        <Btn onClick={() => setAdding(!adding)} style={{ padding: "6px 14px", fontSize: 11 }}>{adding ? "Cancel" : "+ Add"}</Btn>
      </div>

      {!tier.wishlistAlerts && (
        <Card style={{ background: T.statBg }}>
          <div style={{ fontSize: 11, color: T.muted }}>🔒 Upgrade to Quad-8 or Vault Reserve to get alerted when a marketplace listing matches something on your wishlist.</div>
        </Card>
      )}

      {adding && (
        <Card>
          <SectionTitle>New Wishlist Item</SectionTitle>
          <Input label="Album / Title *" value={form.albumTitle} onChange={set("albumTitle")} placeholder="e.g. Abbey Road" />
          <Input label="Artist *" value={form.artist} onChange={set("artist")} placeholder="e.g. The Beatles" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input label="Preferred Year" value={form.preferredYear} onChange={set("preferredYear")} placeholder="1969" type="number" />
            <Input label="Target Price ($)" value={form.targetPrice} onChange={set("targetPrice")} placeholder="0.00" type="number" />
          </div>
          <Input label="Variation" value={form.variation} onChange={set("variation")} placeholder="UK pressing, promo…" />
          <Select label="Priority" value={form.priority} onChange={set("priority")} options={PRIORITIES} />
          <Input label="Notes" value={form.notes} onChange={set("notes")} placeholder="Any additional detail…" />
          <Btn onClick={add} style={{ width: "100%" }}>Add to Wishlist</Btn>
        </Card>
      )}

      {wishlist.map((item) => {
        const match = matchesByWishlistId[item.id];
        return (
          <Card key={item.id} style={match ? { border: `1px solid ${T.accent}` } : {}}>
            {match && (
              <div onClick={() => onViewListing(match)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: T.accent + "18", border: `1px solid ${T.accent}55`, borderRadius: 6, padding: "6px 10px", marginBottom: 10, cursor: "pointer" }}>
                <span style={{ fontSize: 11, color: T.accent, fontWeight: "bold" }}>🎯 Found it! A listing matches this wishlist item.</span>
                <span style={{ fontSize: 11, color: T.accent }}>View →</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: "bold", color: T.text }}>{item.albumTitle}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{item.artist}{item.preferredYear ? ` · ${item.preferredYear}` : ""}</div>
                {item.notes && <div style={{ fontSize: 11, color: T.muted, fontStyle: "italic", marginTop: 4 }}>{item.notes}</div>}
              </div>
              <div style={{ textAlign: "right", marginLeft: 10 }}>
                {item.targetPrice != null && <div style={{ fontSize: 14, fontWeight: "bold", color: T.value }}>${item.targetPrice}</div>}
                <div style={{ fontSize: 9, color: T.muted, marginBottom: 4 }}>TARGET</div>
                <span style={{ fontSize: 10, fontWeight: "bold", color: PRIORITY_COLOR[item.priority] || T.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{item.priority}</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              {removingId === item.id ? (
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn variant="outline" onClick={() => setRemovingId(null)} style={{ padding: "5px 10px", fontSize: 10 }}>Cancel</Btn>
                  <Btn variant="danger" onClick={() => remove(item.id)} style={{ padding: "5px 10px", fontSize: 10 }}>Confirm</Btn>
                </div>
              ) : (
                <Btn variant="outline" onClick={() => setRemovingId(item.id)} style={{ padding: "5px 12px", fontSize: 10 }}>Remove</Btn>
              )}
            </div>
          </Card>
        );
      })}

      {wishlist.length === 0 && !adding && (
        <Card style={{ textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 14, color: T.muted }}>No tapes on your wishlist yet.</div>
        </Card>
      )}
    </div>
  );
}
